import * as React from "react";
import {useState} from "react";
import {alpha, Box, Stack, SxProps, Theme, ThemeProvider} from "@mui/material";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import {useTranslation} from "react-i18next";
import {useTheme} from "@mui/material/styles";
import {validateEmail} from "../../../util/regex";
import UserService from "../../../api/userService";
import {toastError, toastSuccess} from "../toast/Toast";
import {getSafeBackendErrorMessage} from "../../../util/utils";
import TranslateSafeLoadingButton from "../translate-safe-loading-button/TranslateSafeLoadingButton";
import {whiteInputsTheme} from "../../../util/theme/whiteInputsTheme";
import {MailOutlined} from "@mui/icons-material";

interface IProps {
    variant?: "footer" | "blog"
    sx?: SxProps<Theme>
}

const NewsletterSignup = ({variant = "footer", sx}: IProps) => {
    const {t} = useTranslation()
    const theme = useTheme()
    const [email, setEmail] = useState("")
    const [loading, setLoading] = useState(false)
    const [subscribed, setSubscribed] = useState(false)

    const handleSubmit = () => {
        setLoading(true)
        UserService.subscribeNewsletter(email)
            .then(res => {
                toastSuccess(res.data.message)
                setEmail("")
                setSubscribed(true)
            })
            .catch(e => {
                toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
            })
            .finally(() => setLoading(false))
    }

    if (variant === "footer") {
        return (
            <ThemeProvider theme={whiteInputsTheme}>
                <Box sx={sx}>
                    {subscribed ? (
                        <Typography variant="body2" sx={{color: alpha(theme.palette.primary.main, 0.5)}}>
                            ✓ {t('subscribe_newsletter')}
                        </Typography>
                    ) : (
                        <Stack direction="row" spacing={0.75}>
                            <TextField
                                size="small"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && validateEmail(email) && handleSubmit()}
                                label={t('e-mail')}
                                autoComplete="email"
                                sx={{width: {xs: 200, sm: 240}}}
                            />
                            <TranslateSafeLoadingButton
                                loading={loading}
                                variant="contained"
                                color="primary"
                                size="small"
                                disabled={!validateEmail(email)}
                                onClick={handleSubmit}
                                sx={{whiteSpace: "nowrap"}}
                            >
                                {t('submit')}
                            </TranslateSafeLoadingButton>
                        </Stack>
                    )}
                </Box>
            </ThemeProvider>
        )
    }

    // blog variant
    return (
        <ThemeProvider theme={whiteInputsTheme}>
            <Stack
                spacing={1.5}
                sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    borderRadius: '0.75em',
                    backdropFilter: 'blur(5px)',
                    px: {xs: 2.5, sm: 3.5},
                    py: {xs: 2, sm: 2.5},
                    ...sx,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <MailOutlined sx={{color: alpha(theme.palette.primary.main, 0.6), fontSize: '1.3rem'}}/>
                    <Typography variant="h6" color="primary" fontWeight={600}
                                sx={{fontSize: {xs: '1rem', sm: '1.1rem'}}}>
                        {t('newsletter_promo_cta')}
                    </Typography>
                </Stack>
                {subscribed ? (
                    <Typography variant="body2" color="primary" sx={{opacity: 0.6}}>
                        ✓ {t('subscribe_newsletter')}
                    </Typography>
                ) : (
                    <Stack direction={{xs: "column", sm: "row"}} spacing={1}>
                        <TextField
                            fullWidth
                            size="small"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && validateEmail(email) && handleSubmit()}
                            label={t('e-mail')}
                            autoComplete="email"
                        />
                        <TranslateSafeLoadingButton
                            loading={loading}
                            variant="contained"
                            color="primary"
                            size="medium"
                            disabled={!validateEmail(email)}
                            onClick={handleSubmit}
                            sx={{whiteSpace: "nowrap", flexShrink: 0}}
                        >
                            {t('submit')}
                        </TranslateSafeLoadingButton>
                    </Stack>
                )}
            </Stack>
        </ThemeProvider>
    )
}

export default NewsletterSignup
